import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, Download, Eye,
  Loader2, Image, Video, Layers, Wand2, Box, Trash2, ClipboardCopy, MoreVertical, ChevronDown, Smartphone
} from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useClient } from '../../contexts/ClientContext';
import { getApiUrl } from '../../utils/config';
import { ModelPreviewModal } from './ModelPreviewModal';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from '../dialog/ConfirmationDialog';
import AuthenticatedImage from '../media/AuthenticatedImage';
import { useSidebar } from '../../contexts/SidebarContext';
import '@google/model-viewer';

// Declare model-viewer for TypeScript
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'model-viewer': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement> & {
        src?: string;
        alt?: string;
        ar?: boolean;
        'ar-modes'?: string;
        'camera-controls'?: boolean;
        'shadow-intensity'?: string;
        'auto-rotate'?: boolean;
        style?: React.CSSProperties;
      };
    }
  }
}

// Interfaces (assuming these are defined elsewhere, but including for context)
interface AITask {
  id: string; title: string; description: string; status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  task_type: string; provider: string; created_at: string; completed_at?: string; error_message?: string;
  output_media_file_ids?: string[]; input_media_file_id?: string; cost_credits?: number; cost_in_credits?: number; progress_percentage?: number;
  input_data?: { prompt?: string; description?: string; };
}
interface MediaFile { id: string; title: string; original_filename: string; file_url: string; thumbnail_url?: string; mime_type: string; }
interface AITaskListProps { refreshTrigger?: number; }

// Helper Functions
const getStatusVisuals = (status: string) => {
  switch (status) {
    case 'pending': return { Icon: Clock, color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/20' };
    case 'processing': return { Icon: Loader2, color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/20' };
    case 'completed': return { Icon: CheckCircle, color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-500/10', borderColor: 'border-green-500/20' };
    case 'failed': return { Icon: XCircle, color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/20' };
    default: return { Icon: AlertCircle, color: 'text-slate-600 dark:text-slate-400', bgColor: 'bg-slate-500/10', borderColor: 'border-slate-500/20' };
  }
};

const getTaskTypeVisuals = (taskType: string) => {
  switch (taskType) {
    // New clean task type names
    case 'Image Generation (Basic)': return { Icon: Wand2, label: 'Image Studio', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-500/10' };
    case 'Image Generation with Brandify': return { Icon: Layers, label: 'Image Studio - Brandify', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-500/10' };
    case 'Image to 3D Model Generation': return { Icon: Box, label: '3D Studio', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' };
    case '3D Model to AR Ad Generation': return { Icon: Smartphone, label: 'AR Studio', color: 'text-teal-600 dark:text-teal-400', bgColor: 'bg-teal-500/10' };
    case 'Image to Video Generation': return { Icon: Video, label: 'Video Studio', color: 'text-rose-600 dark:text-rose-400', bgColor: 'bg-rose-500/10' };

    case 'Automated Post Scheduling': return { Icon: Clock, label: 'Post Scheduling', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10' };

    // Legacy task type names (for backward compatibility)
    case 'text_to_image': return { Icon: Wand2, label: 'Image Studio', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-500/10' };
    case 'image_processing': return { Icon: Layers, label: 'Image Studio - Brandify', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-500/10' };
    case 'image_enhancement': return { Icon: Layers, label: 'Image Enhancement', color: 'text-indigo-600 dark:text-indigo-400', bgColor: 'bg-indigo-500/10' };
    case 'image_to_3d': return { Icon: Box, label: '3D Studio', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' };
    case 'three_d_to_ar_ad': return { Icon: Box, label: 'AR Studio', color: 'text-teal-600 dark:text-teal-400', bgColor: 'bg-teal-500/10' };
    case 'video_generation': return { Icon: Video, label: 'Video Studio', color: 'text-rose-600 dark:text-rose-400', bgColor: 'bg-rose-500/10' };
    case 'content_generation': return { Icon: Wand2, label: 'Content Generation', color: 'text-amber-600 dark:text-amber-400', bgColor: 'bg-amber-500/10' };
    case 'post_scheduling': return { Icon: Clock, label: 'Post Scheduling', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-500/10' };
    case 'image_analysis': return { Icon: Eye, label: 'Image Analysis', color: 'text-cyan-600 dark:text-cyan-400', bgColor: 'bg-cyan-500/10' };
    case 'three_d_generation': return { Icon: Box, label: '3D Generation', color: 'text-emerald-600 dark:text-emerald-400', bgColor: 'bg-emerald-500/10' };

    default: return { Icon: Image, label: 'AI Task', color: 'text-slate-600 dark:text-slate-400', bgColor: 'bg-slate-500/10' };
  }
};

// Store for task costs - will be populated from API
let taskCostsCache: Record<string, number> = {};

const fetchTaskCosts = async () => {
  try {
    const response = await fetch(`${getApiUrl()}/api/v1/admin/ai-task-costs`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
      },
    });
    if (response.ok) {
      const costs = await response.json();
      taskCostsCache = costs.reduce((acc: Record<string, number>, cost: any) => {
        acc[cost.task_type] = cost.cost_in_credits;
        return acc;
      }, {});
    }
  } catch (error) {
    console.log('Could not fetch task costs, using fallback values');
  }
};

const getEstimatedCost = (taskType: string): string => {
  // Use cached cost if available
  if (taskCostsCache[taskType]) {
    const cost = taskCostsCache[taskType];
    return cost % 1 === 0 ? cost.toString() : cost.toFixed(1);
  }

  // Fallback values based on your actual pricing
  switch (taskType) {
    // New clean task type names
    case 'Image Generation (Basic)': return '10';        // Ad Studio - Image Generation
    case 'Image Generation with Brandify': return '15';  // Ad Studio - Brandify
    case 'Image to 3D Model Generation': return '10';    // Ad Studio AR - 3D Model
    case '3D Model to AR Ad Generation': return '5';     // Ad Studio AR - AR Ad
    case 'Image to Video Generation': return '10';       // Ad Studio - Video

    case 'Automated Post Scheduling': return '2';        // Post Scheduling

    // Legacy task type names (for backward compatibility)
    case 'text_to_image': return '10';        // Ad Studio - Image Generation
    case 'image_processing': return '15';     // Ad Studio - Brandify
    case 'image_enhancement': return '15';
    case 'image_to_3d': return '10';
    case 'three_d_to_ar_ad': return '20';
    case 'video_generation': return '10';
    case 'content_generation': return '5';
    case 'post_scheduling': return '2';
    case 'image_analysis': return '5';
    case 'three_d_generation': return '25';

    default: return '~';
  }
};

// Main Component
export const AITaskList: React.FC<AITaskListProps> = ({ refreshTrigger }) => {
  const { isCollapsed } = useSidebar();
  const [tasks, setTasks] = useState<AITask[]>([]);
  const [loading, setLoading] = useState(true);
  const [taskImages, setTaskImages] = useState<Record<string, MediaFile[]>>({});
  const [inputImages, setInputImages] = useState<Record<string, MediaFile>>({});
  const [isConfirmDeleteDialogOpen, setIsConfirmDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<string | null>(null);

  const [previewImage, setPreviewImage] = useState<MediaFile | null>(null);
  const [preview3DModel, setPreview3DModel] = useState<AITask | null>(null);
  const [previewVideo, setPreviewVideo] = useState<AITask | null>(null);

  const { toast } = useToast();
  const { currentClient } = useClient();
  const navigate = useNavigate();

  const getCurrentClientId = () => currentClient?.id || '';

  const fetchMediaFile = async (fileId: string): Promise<MediaFile | null> => {
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/media/${fileId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}`, 'X-Client-ID': getCurrentClientId() },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch (error) { return null; }
  };

  const fetchAndSetMedia = async (task: AITask) => {
    if (task.output_media_file_ids) {
      const images = await Promise.all(task.output_media_file_ids.map(fetchMediaFile));
      setTaskImages(prev => ({ ...prev, [task.id]: images.filter(Boolean) as MediaFile[] }));
    }
    if (task.input_media_file_id) {
      const inputImage = await fetchMediaFile(task.input_media_file_id);
      if (inputImage) setInputImages(prev => ({ ...prev, [task.input_media_file_id!]: inputImage }));
    }
  };

  const fetchTasks = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}`, 'X-Client-ID': getCurrentClientId() },
      });
      if (!response.ok) throw new Error('Failed to fetch tasks');
      const data = await response.json();
      setTasks(data.tasks || []);
      (data.tasks || []).forEach(fetchAndSetMedia);
    } catch (error) {
      toast({ title: "Error", description: "Failed to load tasks.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchTaskCosts(); // Fetch real costs from API
  }, [refreshTrigger, currentClient]);

  useEffect(() => {
    const hasProcessing = tasks.some(t => ['pending', 'processing'].includes(t.status));
    if (hasProcessing) {
      const interval = setInterval(fetchTasks, 10000);
      return () => clearInterval(interval);
    }
  }, [tasks]);

  const handleDelete = async () => {
    if (!taskToDelete) return;
    const originalTasks = tasks;
    setTasks(tasks.filter(t => t.id !== taskToDelete));
    setIsConfirmDeleteDialogOpen(false);
    try {
      const response = await fetch(`${getApiUrl()}/api/v1/ai/tasks/${taskToDelete}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('access_token')}`, 'X-Client-ID': getCurrentClientId() },
      });
      if (!response.ok) throw new Error('Failed to delete task');
      toast({ title: "Task Deleted", description: "The task has been successfully removed." });
    } catch (error) {
      setTasks(originalTasks);
      toast({ title: "Error", description: "Failed to delete task.", variant: "destructive" });
    }
    setTaskToDelete(null);
  };

  if (loading && tasks.length === 0) {
    return (
      <div className="flex justify-center items-center h-96">
        <Loader2 className="w-16 h-16 animate-spin text-indigo-600 dark:text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:space-y-4 lg:space-y-6 w-full max-w-none">
      {tasks.length === 0 && !loading ? (
        <div className="text-center py-8 sm:py-12 lg:py-16 bg-white/70 dark:bg-slate-800/40 backdrop-blur-xl rounded-xl sm:rounded-2xl lg:rounded-3xl border border-slate-200/50 dark:border-slate-700/50 shadow-2xl">
          <Box className="mx-auto h-10 w-10 sm:h-12 sm:w-12 lg:h-16 lg:w-16 text-slate-400 dark:text-slate-500" />
          <h3 className="mt-3 sm:mt-4 lg:mt-6 text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 dark:text-slate-200">No Tasks Yet</h3>
          <p className="mt-2 text-sm sm:text-base lg:text-lg text-slate-600 dark:text-slate-400 px-4">Your generated tasks will appear here.</p>
        </div>
      ) : (
        tasks.map((task, index) => {
          const { Icon: StatusIcon, color: statusColor, bgColor, borderColor } = getStatusVisuals(task.status);
          const { Icon: TaskIcon, label: taskLabel, color: taskColor, bgColor: taskBgColor } = getTaskTypeVisuals(task.task_type);
          const outputFiles = taskImages[task.id] || [];
          const inputFile = task.input_media_file_id ? inputImages[task.input_media_file_id] : null;

          return (
            <motion.div
              key={task.id}
              className={`relative group bg-white/70 dark:bg-slate-800/60 backdrop-blur-xl border ${borderColor} rounded-xl sm:rounded-2xl lg:rounded-3xl overflow-hidden shadow-2xl shadow-slate-200/50 dark:shadow-2xl hover:shadow-indigo-200/50 dark:hover:shadow-indigo-500/10 transition-all duration-300 w-full max-w-full`}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
            >
              <div className="p-3 sm:p-4 lg:p-6 w-full overflow-hidden">
                <div className="flex items-start justify-between gap-2 sm:gap-3 lg:gap-4 w-full overflow-hidden">
                  <div className="flex items-start gap-2 sm:gap-3 lg:gap-4 flex-1 min-w-0 overflow-hidden">
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 rounded-lg sm:rounded-xl flex items-center justify-center ${bgColor} flex-shrink-0`}>
                      <TaskIcon className={`w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 ${statusColor}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col gap-1 sm:gap-2 mb-1 sm:mb-2">
                        <h4 className="font-bold text-sm sm:text-base lg:text-lg text-black dark:text-slate-100 truncate">{task.title || 'Untitled Task'}</h4>
                        <div className={`px-2 py-1 rounded-lg text-xs font-semibold ${taskBgColor} ${taskColor} self-start w-fit`}>
                          {taskLabel}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 lg:gap-3 text-xs sm:text-sm text-slate-700 dark:text-slate-400">
                        <span>{getTimeAgo(task.created_at)}</span>
                        <span className="hidden sm:inline">&bull;</span>
                        <div className="flex items-center space-x-1">
                          <span className="font-semibold text-amber-600 dark:text-amber-400">
                            {task.status === 'failed' ? 0 : (task.cost_credits || getEstimatedCost(task.task_type))}
                          </span>
                          <span className="text-xs">credits</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Preview for different task types */}
                  {task.task_type === 'Image to 3D Model Generation' && task.status === 'completed' && task.output_data?.model_url ? (
                    <div className="flex-shrink-0">
                      <div className="relative w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600 group/preview bg-gradient-to-br from-emerald-500/20 to-teal-500/20">
                        <div className="w-full h-full flex items-center justify-center">
                          <Box className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 dark:text-emerald-400" />
                        </div>
                        {/* Preview button overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreview3DModel(task);
                            }}
                            className="p-1 text-white hover:text-emerald-400 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="absolute bottom-1 right-1 bg-emerald-600/80 text-white text-xs px-1 rounded">
                          3D
                        </div>
                      </div>
                    </div>
                  ) : task.task_type === 'Image to Video Generation' && task.status === 'completed' && outputFiles.length > 0 ? (
                    <div className="flex-shrink-0">
                      <div className="relative w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600 group/preview bg-gradient-to-br from-rose-500/20 to-pink-500/20">
                        <div className="w-full h-full flex items-center justify-center">
                          <Video className="w-6 h-6 sm:w-8 sm:h-8 text-rose-600 dark:text-rose-400" />
                        </div>
                        {/* Preview button overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewVideo(task);
                            }}
                            className="p-1 text-white hover:text-rose-400 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="absolute bottom-1 right-1 bg-rose-600/80 text-white text-xs px-1 rounded">
                          VIDEO
                        </div>
                      </div>
                    </div>
                  ) : outputFiles.length > 0 ? (
                    <div className="flex-shrink-0">
                      <div className="relative w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 rounded-lg overflow-hidden border border-slate-300 dark:border-slate-600 group/preview">
                        <AuthenticatedImage
                          src={outputFiles[0].thumbnail_url || outputFiles[0].file_url}
                          alt={outputFiles[0].title}
                          className="w-full h-full object-cover"
                          clientId={getCurrentClientId()}
                        />
                        {/* Preview button overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover/preview:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setPreviewImage(outputFiles[0]);
                            }}
                            className="p-1 text-white hover:text-indigo-400 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                        {/* Multiple images indicator */}
                        {outputFiles.length > 1 && (
                          <div className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded">
                            +{outputFiles.length - 1}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : null}

                  <div className="flex flex-col items-end gap-1 sm:gap-2 flex-shrink-0 min-w-0">
                    <div className={`flex items-center gap-1 sm:gap-2 px-2 py-1 rounded-full text-xs font-medium ${bgColor} ${borderColor}`}>
                      <StatusIcon className={`w-3 h-3 sm:w-4 sm:h-4 ${statusColor} ${task.status === 'processing' ? 'animate-spin' : ''}`} />
                      <span className={`${statusColor} truncate`}>{task.status}</span>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setTaskToDelete(task.id);
                        setIsConfirmDeleteDialogOpen(true);
                      }}
                      className="p-1 sm:p-1.5 lg:p-2 rounded-full hover:bg-red-500/20 text-slate-600 dark:text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" />
                    </button>
                  </div>
                </div>


              </div>
              {task.status === 'processing' && (
                <div className="absolute bottom-0 left-0 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 animate-pulse rounded-b-3xl" style={{ width: `${task.progress_percentage || 0}%` }}></div>
              )}
            </motion.div>
          );
        })
      )}

      <ConfirmationDialog
        isOpen={isConfirmDeleteDialogOpen}
        onClose={() => setIsConfirmDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Confirm Task Deletion"
        message="Are you sure you want to permanently delete this task? This action cannot be undone."
      />

      {/* Image Preview Modal */}
      {previewImage && (
        <div className={`fixed inset-0 lg:left-16 xl:${isCollapsed ? 'left-16' : 'left-64'} z-50 transition-all duration-300`} onClick={() => setPreviewImage(null)}>
          {/* Mobile Layout */}
          <div className="lg:hidden fixed inset-0 bg-black/95 flex flex-col">
            {/* Mobile Header */}
            <div className="flex items-center justify-between p-4 text-white">
              <h3 className="text-lg font-medium truncate pr-4">{previewImage.title || previewImage.original_filename}</h3>
              <button
                onClick={() => setPreviewImage(null)}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            
            {/* Mobile Image container */}
            <div className="flex-1 flex items-center justify-center p-4">
              <AuthenticatedImage
                src={previewImage.file_url}
                alt={previewImage.title}
                className="max-w-full max-h-full object-contain rounded-lg"
                clientId={getCurrentClientId()}
              />
            </div>
            
            {/* Mobile Bottom area */}
            <div className="p-4 text-center">
              <p className="text-white/70 text-sm">Tap anywhere to close</p>
            </div>
          </div>

          {/* Desktop Layout - Original Design */}
          <div className="hidden lg:flex bg-black/80 backdrop-blur-sm h-full items-center justify-center p-4">
            <div className="relative max-w-4xl max-h-[90vh] bg-white dark:bg-slate-800 rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{previewImage.title || previewImage.original_filename}</h3>
                <button
                  onClick={() => setPreviewImage(null)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <XCircle className="w-5 h-5 text-slate-500" />
                </button>
              </div>
              <div className="p-4">
                <AuthenticatedImage
                  src={previewImage.file_url}
                  alt={previewImage.title}
                  className="max-w-full max-h-[70vh] object-contain mx-auto rounded-lg"
                  clientId={getCurrentClientId()}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 3D Model Preview Modal */}
      {preview3DModel && (
        <div className={`fixed inset-0 lg:left-16 xl:${isCollapsed ? 'left-16' : 'left-64'} bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 transition-all duration-300`} onClick={() => setPreview3DModel(null)}>
          <div className="relative w-full h-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] bg-white dark:bg-slate-800 rounded-lg sm:rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-2 sm:p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <Box className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                <h3 className="text-sm sm:text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">{preview3DModel.title}</h3>
              </div>
              <button
                onClick={() => setPreview3DModel(null)}
                className="p-1 sm:p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
              >
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-2 sm:p-6 h-[calc(100%-120px)] sm:h-auto">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-xl p-2 sm:p-4 h-full sm:min-h-[300px] lg:min-h-[400px] flex items-center justify-center">
                <model-viewer
                  src={`${getApiUrl()}${preview3DModel.output_data?.model_url}`}
                  alt="Generated 3D Model"
                  ar
                  ar-modes="webxr scene-viewer quick-look"
                  camera-controls
                  shadow-intensity="1"
                  auto-rotate
                  style={{
                    width: '100%',
                    height: '100%',
                    minHeight: '250px',
                    backgroundColor: 'transparent'
                  }}
                />
              </div>
              <div className="mt-2 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                  Interactive 3D model - drag to rotate, scroll to zoom
                </div>
                <a
                  href={`${getApiUrl()}${preview3DModel.output_data?.model_url}`}
                  download
                  className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-lg transition-colors"
                >
                  <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                  Download GLB
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Preview Modal */}
      {previewVideo && (
        <div className={`fixed inset-0 lg:left-16 xl:${isCollapsed ? 'left-16' : 'left-64'} bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 transition-all duration-300`} onClick={() => setPreviewVideo(null)}>
          <div className="relative w-full h-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] bg-white dark:bg-slate-800 rounded-lg sm:rounded-2xl overflow-hidden shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-2 sm:p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <Video className="w-4 h-4 sm:w-5 sm:h-5 text-rose-600 dark:text-rose-400 flex-shrink-0" />
                <h3 className="text-sm sm:text-lg font-semibold text-slate-900 dark:text-slate-100 truncate">{previewVideo.title}</h3>
              </div>
              <button
                onClick={() => setPreviewVideo(null)}
                className="p-1 sm:p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors flex-shrink-0"
              >
                <XCircle className="w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
              </button>
            </div>
            <div className="p-2 sm:p-6 h-[calc(100%-120px)] sm:h-auto">
              <div className="bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-700 dark:to-slate-800 rounded-xl p-2 sm:p-4 h-full sm:h-auto">
                {previewVideo.output_media_file_ids && previewVideo.output_media_file_ids.length > 0 && (
                  <video
                    src={`${getApiUrl()}/api/v1/media/${previewVideo.output_media_file_ids[0]}/download`}
                    controls
                    autoPlay
                    loop
                    className="w-full h-full sm:h-auto sm:max-h-[400px] rounded-lg object-contain"
                  />
                )}
              </div>
              <div className="mt-2 sm:mt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                <div className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                  Generated video - click to play/pause
                </div>
                {previewVideo.output_media_file_ids && previewVideo.output_media_file_ids.length > 0 && (
                  <a
                    href={`${getApiUrl()}/api/v1/media/${previewVideo.output_media_file_ids[0]}/download`}
                    download
                    className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-rose-600 hover:bg-rose-700 text-white text-sm rounded-lg transition-colors"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4" />
                    Download Video
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getTimeAgo = (dateString: string) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  return `${Math.floor(diffInMinutes / 1440)}d ago`;
};

export default AITaskList;
